/**
 * Ordena alfabéticamente un array de objetos por una clave dada,
 * con manejo correcto de:
 *   - Acentos (los normaliza para que "ñoño" y "nono" comparen igual).
 *   - Mayúsculas/minúsculas (insensible).
 *   - Números al inicio del string (los ordena numéricamente, no lexicográficamente:
 *     "10 cosas" va después de "2 cosas", no antes).
 *   - Valores null/undefined (van al final).
 *   - Locale español (usa localeCompare con 'es').
 *
 * No muta el array original — devuelve uno nuevo.
 *
 * @param data Array a ordenar
 * @param key  Nombre de la propiedad por la que ordenar
 *
 * @example
 *   sortAlphabetically([{name: 'banana'}, {name: 'Ávila'}, {name: '10 cosas'}], 'name')
 *   // → [{name: '10 cosas'}, {name: 'Ávila'}, {name: 'banana'}]
 */
export function sortAlphabetically<T>(data: readonly T[], key: keyof T): T[] {
  // Por nombre: si el catálogo tiene alias, primero alias y luego nombre.
  if (key === 'name') return sortCatalog([...data]);
  const sortedData = [...data];

  const normalize = (str: unknown): string =>
    str
      ?.toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')   // remover acentos/diacríticos
      .replace(/\s+/g, ' ')              // colapsar espacios múltiples
      .trim()
      .toLowerCase() ?? '';

  sortedData.sort((a, b) => {
    const valueA = normalize(a?.[key]);
    const valueB = normalize(b?.[key]);

    // Vacíos al final
    if (!valueA && !valueB) return 0;
    if (!valueA) return 1;
    if (!valueB) return -1;

    // Si empiezan con número, ordenar numéricamente por ese número
    const numA = parseInt(valueA, 10);
    const numB = parseInt(valueB, 10);

    if (!isNaN(numA) && !isNaN(numB) && numA !== numB) {
      return numA - numB;
    }
    if (!isNaN(numA) && isNaN(numB)) return -1;
    if (isNaN(numA) && !isNaN(numB)) return 1;

    // Texto puro (sin número inicial si lo había) — comparación con locale
    const textA = valueA.replace(/^\d+\s*/, '');
    const textB = valueB.replace(/^\d+\s*/, '');

    return textA.localeCompare(textB, 'es', {
      sensitivity:       'base',
      ignorePunctuation: true,
    });
  });

  return sortedData;
}

/** Orden de los campos para comparar: alias primero, luego nombre, código… */
const SORT_FIELDS = ['alias', 'name', 'label', 'lastName', 'code', 'sapCode', 'serialNumber', 'description'];
const collator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

/** Ordena un catálogo: alias → nombre → código… (vacíos al final, sin acentos). */
export function sortCatalog<R>(rows: R[]): R[] {
  const key = (row: any, field: string) => String(row?.[field] ?? '').trim();
  return [...rows].sort((a: any, b: any) => {
    for (const field of SORT_FIELDS) {
      const x = key(a, field), y = key(b, field);
      if (x === y) continue;
      if (!x) return 1;
      if (!y) return -1;
      const c = collator.compare(x, y);
      if (c !== 0) return c;
    }
    return 0;
  });
}
